/**
 * Microsoft Authentication Library (MSAL) setup for the StyxDesk SPA.
 *
 * The single source of truth for "are we running in real-auth mode or in
 * dev-fallback mode". Read by Login.tsx, api.ts, main.tsx, and AppShell.tsx.
 *
 * Behaviour:
 *   - If `VITE_ENTRA_CLIENT_ID` is set at build time → real MSAL flow
 *     (Authorization Code + PKCE redirect). `pca` is a configured
 *     PublicClientApplication; `isMsalActive()` returns true.
 *   - If `VITE_ENTRA_CLIENT_ID` is NOT set (typical local dev) → MSAL is
 *     not instantiated; the SPA falls back to the existing `X-Dev-User`
 *     header flow. `pca` is null; `isMsalActive()` returns false.
 *
 * Why a runtime guard rather than a build-time strip-out:
 *   We want one bundle that works in both modes. Engineers can drop a
 *   `VITE_ENTRA_CLIENT_ID=…` into `frontend/.env.local` and rebuild to
 *   test the MSAL flow against the dev app registration without
 *   touching any source.
 *
 * Backend contract (see backend/app/core/entra.py):
 *   - The access token's `aud` claim MUST equal `VITE_ENTRA_AUDIENCE`
 *     (typically `api://<client-id>`).
 *   - The token's `iss` claim MUST equal the v2.0 issuer for the tenant:
 *     `https://login.microsoftonline.com/<tenant-id>/v2.0`.
 *   - We request the scope `<audience>/access_as_user` so the backend
 *     receives an access token (not just an id_token).
 *
 * Storage strategy:
 *   sessionStorage — clears on tab close, scoped per-tab. We deliberately
 *   do NOT use localStorage: smaller XSS blast radius, and CLAUDE.md
 *   §6.2 wants sessions to expire on browser close anyway.
 *
 * MSAL react peer-dep warning:
 *   @azure/msal-react@2 declares peer react@"^16.8.0 || ^17 || ^18", but
 *   we're on React 19. The library works fine — the manifest just lags.
 *   See https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/7234
 */
import { PublicClientApplication, InteractionRequiredAuthError, LogLevel, } from '@azure/msal-browser';
// ---- Env knobs -----------------------------------------------------------
const TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID;
const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID;
const API_SCOPE = import.meta.env.VITE_ENTRA_API_SCOPE;
// Optional override; defaults to https://login.microsoftonline.com/<tenant>
const AUTHORITY = import.meta.env.VITE_ENTRA_AUTHORITY ||
    (TENANT_ID ? `https://login.microsoftonline.com/${TENANT_ID}` : undefined);
// ---- Active-flag ---------------------------------------------------------
/** True iff the env vars needed to instantiate MSAL are all present. */
export function isMsalActive() {
    return !!(TENANT_ID && CLIENT_ID && AUTHORITY);
}
// ---- Client --------------------------------------------------------------
/**
 * The PublicClientApplication instance, or null when MSAL is not configured.
 * `pca` is null in local dev when VITE_ENTRA_CLIENT_ID is unset; consumers
 * must guard with `if (pca)` or `if (isMsalActive())` before calling.
 */
export const pca = isMsalActive()
    ? new PublicClientApplication({
        auth: {
            clientId: CLIENT_ID,
            authority: AUTHORITY,
            // Both /auth/callback (login) and /auth/logout (sign-out landing).
            // Must match what's registered in the Entra app registration.
            redirectUri: `${window.location.origin}/auth/callback`,
            postLogoutRedirectUri: `${window.location.origin}/login`,
            // Single-tenant by design — the deploy IDs are tenant-scoped.
            navigateToLoginRequestUrl: true,
        },
        cache: {
            cacheLocation: 'sessionStorage',
            // Don't use cookies — modern browsers don't strictly need them for
            // PKCE redirect flows, and skipping them avoids SameSite gotchas.
            storeAuthStateInCookie: false,
        },
        system: {
            loggerOptions: {
                // Pipe MSAL diagnostics into the browser console (filtered).
                // In prod, App Insights will pick these up automatically.
                loggerCallback: (level, message, containsPii) => {
                    if (containsPii)
                        return;
                    if (level === LogLevel.Error)
                        console.error('[msal]', message);
                    else if (level === LogLevel.Warning)
                        console.warn('[msal]', message);
                    // Info / Verbose suppressed in client; backend gets the equivalent
                    // via /api/v1/audit-logs once the user is signed in.
                },
                logLevel: LogLevel.Warning,
                piiLoggingEnabled: false,
            },
        },
    })
    : null;
// ---- Request shapes ------------------------------------------------------
// We deliberately request **only OIDC scopes** here, not a custom API scope.
//
// For an SPA + backend sharing one Entra app registration, the recommended
// pattern is to send the **id_token** as the bearer. id_tokens carry
// `aud = <clientId>` and `iss = https://login.microsoftonline.com/<tid>/v2.0`
// — exactly what app/core/entra.py validates.
//
// Requesting `api://<clientId>/access_as_user` instead would fail with
// **AADSTS500011** unless an Entra admin had configured "Expose an API"
// on the app registration. For single-tenant Phase-1, id-token auth gives
// the same security envelope (same iss, same tid, same signature key, just
// a different `aud`) without the admin-portal detour.
//
// API_SCOPE is still read by env-detection (isMsalActive) so its presence
// flips MSAL mode on; we just don't request it on the wire.
void API_SCOPE;
const OIDC_SCOPES = ['openid', 'profile', 'email'];
export const loginRequest = {
    scopes: OIDC_SCOPES,
    // Prompting only on first sign-in; subsequent silent acquisitions reuse
    // the cached refresh token.
    prompt: 'select_account',
};
/**
 * Silent-acquisition request used by api.ts on every protected call.
 * Caller fills in `account` from msal.getActiveAccount().
 */
export const silentRequestTemplate = {
    scopes: OIDC_SCOPES,
};
// ---- Lifecycle hook ------------------------------------------------------
/**
 * One-time bootstrap. Called from main.tsx before <App> mounts.
 * Returns a promise that resolves once MSAL has processed any inbound
 * redirect-response (i.e. the user is coming back from /auth/callback).
 *
 * Safe to call when MSAL is inactive — resolves immediately.
 */
export async function initMsal() {
    if (!pca)
        return;
    await pca.initialize();
    // Process any redirect that just landed us back on the SPA. This sets
    // the active account in msal's cache as a side-effect.
    const result = await pca.handleRedirectPromise();
    if (result?.account) {
        pca.setActiveAccount(result.account);
    }
    else {
        // Existing session in cache from a prior tab/visit — pick the first.
        const accounts = pca.getAllAccounts();
        if (accounts.length > 0 && !pca.getActiveAccount()) {
            pca.setActiveAccount(accounts[0] ?? null);
        }
    }
    // Start the proactive id_token refresh once we have an active session, so
    // the bearer never lapses mid-session (see the timer block below). No-op
    // when there's still no account (pre-login on the /login route).
    if (pca.getActiveAccount()) {
        startTokenAutoRefresh();
    }
}
/** Read the active account's id-token `exp` claim (Unix epoch seconds)
 *  without making a network call. Used by the SessionIndicator chip
 *  to show "session expires in N minutes". The proactive-refresh timer
 *  below force-refreshes the id_token before this exp is reached and
 *  calls setActiveAccount, so each tick re-reads the latest (extended) exp.
 *
 *  Returns null when:
 *   - MSAL isn't configured (local dev with X-Dev-User)
 *   - No active account yet (pre-login)
 *   - idTokenClaims missing exp (defensive — shouldn't happen) */
export function getActiveSessionExpiry() {
    if (!pca)
        return null;
    const account = pca.getActiveAccount();
    const exp = account?.idTokenClaims?.exp;
    return typeof exp === 'number' ? exp : null;
}
// ---- Proactive token refresh --------------------------------------------
// Why this exists:
//   We send the **id_token** as the API bearer (see the request-shapes note
//   above). For OIDC-only scopes, MSAL's acquireTokenSilent WITHOUT
//   forceRefresh returns the CACHED id_token — it does not silently mint a
//   fresh one as it does for resource access-tokens. So the id_token simply
//   lapses at its ~1h `exp`, and the next API call gets a 401 "Signature has
//   expired" (the recurring hourly logout symptom). forceRefresh:true DOES
//   mint a fresh id_token via the refresh token.
//
//   This timer force-refreshes ~5 min BEFORE exp so the token never lapses
//   mid-session. It's purely additive: it does not change the login/redirect
//   flow or the per-request authHeader(); api.ts still has its reactive
//   401-retry as a backstop for the rare case this timer can't cover.
let _refreshTimer = null;
/** Refresh this many seconds before the id_token's `exp`. 5 min mirrors
 *  MSAL's own access-token refresh skew and the SessionIndicator "critical"
 *  threshold. */
const REFRESH_SKEW_SECONDS = 300;
/** Never schedule the refresh tighter than this — avoids a tight loop when
 *  the active token is already inside the skew window at startup. */
const MIN_REFRESH_DELAY_MS = 30_000;
/** Backoff before retrying after a transient (non-interactive) refresh error. */
const TRANSIENT_RETRY_MS = 60_000;
/** Cancel any pending proactive refresh. Safe to call when none is scheduled
 *  (e.g. on sign-out). */
export function stopTokenAutoRefresh() {
    if (_refreshTimer !== null) {
        clearTimeout(_refreshTimer);
        _refreshTimer = null;
    }
}
async function refreshNowAndReschedule() {
    if (!pca)
        return;
    const account = pca.getActiveAccount();
    if (!account)
        return;
    try {
        const result = await pca.acquireTokenSilent({
            ...silentRequestTemplate,
            account,
            // Bypass the cache and use the refresh token to mint a brand-new
            // id_token. This is the whole point of the proactive timer.
            forceRefresh: true,
        });
        if (result.account)
            pca.setActiveAccount(result.account);
        // Success → schedule the next refresh against the NEW (extended) exp.
        startTokenAutoRefresh();
    }
    catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
            // The refresh token itself is dead — only an interactive login fixes
            // this. We deliberately do NOT acquireTokenRedirect from a background
            // timer (a surprise full-page redirect mid-task is hostile). Stop the
            // loop; the next API call's 401-retry path in api.ts will redirect at
            // a natural request boundary.
            console.warn('[msal] proactive refresh needs interaction; 401-retry will redirect.');
            stopTokenAutoRefresh();
        }
        else {
            // Transient (offline / MSAL busy). Try again shortly; if it keeps
            // failing the token lapses and the reactive 401 path takes over.
            console.warn('[msal] proactive refresh transient error; retrying in 60s:', err);
            stopTokenAutoRefresh();
            _refreshTimer = setTimeout(() => void refreshNowAndReschedule(), TRANSIENT_RETRY_MS);
        }
    }
}
/** (Re)schedule the proactive refresh based on the current id_token exp.
 *  Idempotent — clears any existing timer first. No-op when MSAL is inactive
 *  or there's no active session yet. Called once after bootstrap (initMsal)
 *  and then self-reschedules after each successful refresh. */
export function startTokenAutoRefresh() {
    if (!pca)
        return;
    stopTokenAutoRefresh();
    const exp = getActiveSessionExpiry();
    if (exp === null)
        return;
    const fireInMs = Math.max(MIN_REFRESH_DELAY_MS, (exp - REFRESH_SKEW_SECONDS) * 1000 - Date.now());
    _refreshTimer = setTimeout(() => void refreshNowAndReschedule(), fireInMs);
}
